import React, { useContext, useEffect, useRef, useState } from 'react';
import './CategoryIcons.css';
import CategoryIcon from '../../Category/Icon/CategoryIcon.jsx';
import { useNavigate } from 'react-router-dom';
import { CategoriesContext } from '../../../services/context/CategoriesContext.js';
import ROUTES from '../../../services/Constants/GlobalRoutes.jsx';
import { defaultAnalyticsVariables, events, pagePrefix } from '../../../services/Constants/Analytics.js';
import { AnalyticsPageContext } from '../../../services/context/AnalyticsPageContext.js';
import { UserDetailsContext } from '../../../services/context/UserDetailsContext.js';
import { SubscriptionDetailsContext } from '../../../services/context/SubscriptionDetailsContext.js';
import { UpdateSnackBarContext } from '../../../services/context/SnackBarContext.js';
import FailIcon from '../../../assets/FailIcon.jsx';
import useNormalizedSubscriptionStatus from '../../../services/utils/subscriptionUtils.js';

/**
 * @returns {JSX.Element} CategoryIcons Component
 */
export default function CategoryIcons() {
  const categories = useContext(CategoriesContext);
  const navigateTo = useNavigate();

  const [scrollX, setScrollX] = useState(0);
  const [touched, setTouched] = useState(false);
  const scrollInterval = useRef(null);
  const scrollForwardTimeout = useRef(null);
  const scrollBackwardTimeout = useRef(null);
  const scrollStopTimeout = useRef(null);
  const listRef = useRef(null);
  const userDetails = useContext(UserDetailsContext);
  const subscriptionDetails = useContext(SubscriptionDetailsContext);
  const setSnackBar = useContext(UpdateSnackBarContext);
  const subscriptionStatus = useNormalizedSubscriptionStatus();

  useEffect(() => {
    if (!touched && scrollX) {
      scrollInterval.current = setInterval(() => {
        listRef.current.scrollBy(scrollX, 0);
      }, 70);
    }

    return () => clearInterval(scrollInterval.current);
  }, [
    touched,
    scrollX,
  ]);

  useEffect(() => {
    if (!touched) {
      scrollForwardTimeout.current = setTimeout(() => {
        setScrollX(1);
        scrollBackwardTimeout.current = setTimeout(() => {
          setScrollX(-1);
          scrollStopTimeout.current = setTimeout(() => {
            setScrollX(0);
          }, 1000);
        }, 1000);
      }, 10_000);
    }

    return () => {
      clearTimeout(scrollForwardTimeout.current);
      clearTimeout(scrollBackwardTimeout.current);
      clearTimeout(scrollStopTimeout.current);
    };
  }, [
    touched,
    scrollStopTimeout.current,
  ]);

  const analyticsPage = useContext(AnalyticsPageContext);
  const onClick = category => {
    window.utag?.link({
      ...defaultAnalyticsVariables,
      page_name: analyticsPage,
      event_name: [events.interaction],
      link_id: `${pagePrefix}: shop by category: ${category.name}`,
    });

    if (category.isClubCategory) {
      if (subscriptionDetails?.expiryDate) {
        navigateTo(ROUTES.CATEGORY.get(category.id));
      } else {
        window.utag?.link({
          ...defaultAnalyticsVariables,
          event_name: [events.error],
          page_name: analyticsPage,
          event_error_name: 'Service unavailable now. Please try again.',
          link_id: `${pagePrefix}: shop by category: ${category.name}`,
          visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
        });
        setSnackBar({
          icon: <FailIcon />,
          body: 'Service unavailable now. Please try again.',
        });
      }
    } else {
      navigateTo(ROUTES.CATEGORY.get(category.id));
    }
  };

  const loading = categories.length === 0;
  const showVodapayCategoryIcon = userDetails.vsp === false || (subscriptionDetails?.status === 200 && !subscriptionDetails?.isSubscribed
                                  && !subscriptionDetails?.isEligibleToSubscribe);

  return (
    <section className='category-icons v-container'>
      <header className={ `container-v${loading ? ' empty' : ''}` }>
        <h3 className={ `${loading ? 'skeleton-loader' : ''}` }>{loading ? '' : 'Shop by category'}</h3>
      </header>
      <ul
        className='container'
        ref={ listRef }
        onTouchStart={ () => setTouched(true) }
      >
        {
          categories.map(category => ((showVodapayCategoryIcon && category.isClubCategory) ? null : (
            <button
              key={ category.id }
              onClick={ () => onClick(category) }
            >
              <CategoryIcon category={ category } />
            </button>
          )))
        }
        {
          loading && Array.from({ length: 10 }).map((_, index) => (
            <div
              className='container'
              key={ index }
            ><CategoryIcon /></div>
          ))
        }
      </ul>
    </section>
  );
}
